// Seed data ported verbatim from design_handoff/prototype/data.js.
// Used by the mock API layer and as a fallback when the real backend
// returns empty (e.g. fresh dev orgs with no Runs yet).
//
// The shapes here MUST match lib/types.ts. When the backend contract
// in BACKEND.md changes, update both this file and types.ts.

import type {
  ExploreHistoryItem,
  ExploreQuery,
  ModeOption,
  PhaseDefinition,
  PipelineStep,
  Playbook,
  PlaybookContent,
  Profile,
  QuickStart,
  Run,
  Story,
  Template,
  Volume,
} from "@/lib/types";

export const PACKS: Profile[] = [
  {
    id: "cirrus_renewable_energy",
    name: "Cirrus Renewable Energy",
    industry: "Clean Energy",
    products: 6,
    clouds: ["Sales", "Service"],
    stories: 16,
    logo: "/assets/pack-cirrus.png",
    description:
      "Solar, wind, and battery storage — full Sales + Service demo with pipeline health, CSAT, and agent performance.",
    accent: "oklch(0.72 0.14 220)",
    useCount: 1284,
    featured: true,
    type: "official",
  },
  {
    id: "northern_trail",
    name: "Northern Trail Outfitters",
    industry: "Retail",
    products: 5,
    clouds: ["Sales", "Service"],
    stories: 12,
    logo: "/assets/pack-nto.png",
    description:
      "Outdoor retail with seasonal trends, regional gaps, and omni-channel service stories.",
    accent: "oklch(0.70 0.13 30)",
    useCount: 642,
    featured: true,
    type: "official",
  },
  {
    id: "bolt_bikes",
    name: "Bolt Bikes D2C",
    industry: "Direct-to-Consumer",
    products: 8,
    clouds: ["Sales", "Service"],
    stories: 14,
    logo: "/assets/pack-bolt.png",
    description:
      "Electric bikes sold direct, with subscription service tiers and warranty claim stories.",
    accent: "oklch(0.74 0.13 150)",
    useCount: 318,
    featured: false,
    type: "official",
  },
];

// Volume presets MUST match ``VOLUME_PRESETS`` in ``pipeline_runner.py`` —
// the backend resolves these IDs to actual generator counts (``num_accounts``
// / ``num_opportunities`` / ``num_cases``) and silently falls back to the
// ``small`` preset on any ID it doesn't recognise.  The previous numbers
// here were aspirational marketing values that bore no resemblance to what
// the worker actually generated, which made the Monitor "Targets" strip lie
// (showed 900 accounts while the run was provisioning 200).
//
// ``duration`` is a UX-only estimate based on observed worker walltimes
// for a fresh org with both clouds enabled and 16 stories selected.
export const VOLUMES: Volume[] = [
  { id: "tiny",   label: "Tiny",   accounts: 50,  opps: 300,   cases: 1000,  duration: 3,  desc: "Smoke-test or quick screen share" },
  { id: "small",  label: "Small",  accounts: 75,  opps: 500,   cases: 3000,  duration: 8,  desc: "Single-persona demo, 15-min call" },
  { id: "medium", label: "Medium", accounts: 200, opps: 3000,  cases: 20000, duration: 14, desc: "Full deck, dashboards feel alive" },
  { id: "large",  label: "Large",  accounts: 300, opps: 5000,  cases: 30000, duration: 22, desc: "Exec review / enterprise POV" },
  { id: "xlarge", label: "XL",     accounts: 500, opps: 10000, cases: 50000, duration: 35, desc: "Scale / perf testing" },
];

export const STORIES: Story[] = [
  { id: "pipeline_health",    cloud: "sales",   title: "Pipeline health crisis",      desc: "Deals slipping. Show where and why.", tags: ["exec","rev-ops"] },
  { id: "rep_ramp",           cloud: "sales",   title: "Rep ramp trajectory",         desc: "New hires vs. tenured — quota to trend.", tags: ["enablement"] },
  { id: "product_mix",        cloud: "sales",   title: "Product mix shift",           desc: "Revenue rotating from hardware to services.", tags: ["cfo","product"] },
  { id: "lead_roi",           cloud: "sales",   title: "Lead source ROI",             desc: "Which channels actually convert.", tags: ["marketing"] },
  { id: "regional_gap",       cloud: "sales",   title: "Regional performance gap",    desc: "East crushing it, West stalling.", tags: ["exec"] },
  { id: "forecast_accuracy",  cloud: "sales",   title: "Forecast accuracy",           desc: "Commit vs. close across quarters.", tags: ["cfo","rev-ops"] },
  { id: "account_turnaround", cloud: "sales",   title: "Account turnaround",          desc: "Red-flagged account rescued with multi-threading.", tags: ["csm"] },
  { id: "csat_transform",     cloud: "service", title: "CSAT transformation",         desc: "From 3.1 to 4.6 in two quarters.", tags: ["exec"] },
  { id: "agent_spotlight",    cloud: "service", title: "Agent performance spotlight", desc: "Top agents vs. cohort.", tags: ["ops"] },
  { id: "regional_service",   cloud: "service", title: "Regional service quality",    desc: "Where the product breaks, by zip.", tags: ["product","ops"] },
  { id: "response_csat",      cloud: "service", title: "Response time → CSAT",        desc: "Every minute of wait costs satisfaction.", tags: ["ops"] },
  { id: "omni_channel",       cloud: "service", title: "Omni-channel optimization",   desc: "Chat + phone + email deflection.", tags: ["ops","digital"] },
  { id: "knowledge_roi",      cloud: "service", title: "Knowledge base ROI",          desc: "Articles saving 1,200 tickets/mo.", tags: ["cx"] },
  { id: "product_support",    cloud: "service", title: "Product support excellence",  desc: "SKU-level support quality.", tags: ["product","ops"] },
  { id: "sla_journey",        cloud: "service", title: "SLA compliance journey",      desc: "Miss rate collapsed after triage redesign.", tags: ["ops"] },
  { id: "premium_roi",        cloud: "service", title: "Premium support ROI",         desc: "Tier justifies its price.", tags: ["cfo","cx"] },
];

// Phase step labels are intentionally count-free here — the worker
// surfaces the real, run-specific counts via `current_step` (e.g.
// "generate_opportunities · 2,400 / 5,000"). Hardcoding "Accounts (900)"
// here was lying when a tiny-volume run targets only 50 records.
export const PHASES: PhaseDefinition[] = [
  { id: "auth",     label: "Authenticate org",        steps: ["Verify OAuth token", "Check scopes", "Fetch org limits"] },
  { id: "metadata", label: "Phase 1 · Metadata",      steps: ["Deploy custom fields", "Deploy record types", "Page layouts", "Permission sets"] },
  { id: "found",    label: "Phase 2 · Foundation",    steps: ["Accounts", "Contacts", "Users + roles", "Territories"] },
  { id: "cases",    label: "Phase 3 · Cases",         steps: ["Generate case archetypes", "Cases", "Case comments", "Email messages"] },
  { id: "hist",     label: "Phase 3b · Case history", steps: ["Status transitions", "SLA milestones", "Agent work events"] },
  { id: "sales",    label: "Phase 4 · Sales Cloud",   steps: ["Opportunities", "Line items", "Opp history", "Campaigns + members"] },
  { id: "salesb",   label: "Phase 4b · Sales data",   steps: ["Forecast quotas", "Leads", "Sales activities"] },
  { id: "post",     label: "Phase 5 · Post-process",  steps: ["Date shifting", "Ownership resolution", "Last-activity dates"] },
  { id: "dc",       label: "Phase 6 · Data 360",      steps: ["Provision Data 360", "Deploy data kits", "Refresh data streams", "Tableau Next workspaces"] },
];

export const QUICK_STARTS: QuickStart[] = [
  { id: "quick", label: "Quick demo",     desc: "Cirrus · Small · 4 greatest-hits stories",      time: "~8 min",  icon: "lightning" },
  { id: "full",  label: "Full Sales+Svc", desc: "Cirrus · Medium · all 16 stories",              time: "~14 min", icon: "sparkles" },
  { id: "exec",  label: "Exec review",    desc: "Cirrus · Large · 7 exec-flavored stories + DC", time: "~22 min", icon: "target" },
  { id: "cx",    label: "Service focus",  desc: "Cirrus · Medium · Service-only stories",        time: "~12 min", icon: "headset" },
];

export const MODES: ModeOption[] = [
  { id: "crm_only",  label: "Load CRM data",          tag: "Fast",        blurb: "Populate a connected Salesforce org. Accounts, opps, cases, contacts — that's it.", time: "~8–22 min", pipeline: ["crm"] },
  { id: "tdsx_only", label: "Generate TDSX only",     tag: "No org",      blurb: "Download a Tableau datasource. Use when you don't have an org connected or want to demo in Tableau Desktop.", time: "~5 min", pipeline: ["tdsx"] },
  { id: "full",      label: "Full pipeline",          tag: "Complete",    blurb: "Load CRM, then push to Data 360, deploy C360 SDM, sample apps, and Tableau Next workspaces. Full demo.", time: "~22–40 min", pipeline: ["crm","dc","sdm","apps","tabnext"] },
  { id: "continue",  label: "Add to an existing run", tag: "Follow-up",   blurb: "Already loaded data? Come back and layer on Data 360, apps, or Tableau Next without rebuilding from scratch.", time: "~variable", pipeline: ["dc","sdm","apps","tabnext"] },
  { id: "refresh",   label: "Refresh / extend",       tag: "Maintenance", blurb: "Refresh Data 360 streams, or add Service to a Sales-only org you already built.", time: "~3–8 min", pipeline: ["refresh"] },
];

// 2026-05-12 (GA): the SDM + Insights Apps tiles are live. C360 SDM
// ships ``sfdc_internal__C360UnifiedSemanticModel`` cleanly (verified
// SuccessStatus on bolt-bikes + data-depot in
// ``OVERNIGHT_STATUS_2026-04-30.md``); the cascade fail-fast and
// damaged-org preflight in ``app/services/atf_deployer.py`` guard any
// edge-case failure into a clean stop rather than a partial-org mess.
// The `apps` tile now activates the canonical Sales/Service Insights
// pair (``SalesInsightsTN`` + ``Service_Insights_withEDCFORTC``) plus
// the TC26 Sample Sales Cloud Dashboard package. Both pre-release
// blockers (Engaged_Articles_clc reference + KQ_SurveyQuestionId__c
// schema drift) are resolved — see ``docs/KNOWN_ISSUES.md`` and the
// ``scripts/patch_atf_si_survey_kq_drift.py`` patch script. Embedded
// Sales Insights stays gated off in real.ts because it requires the
// ``User_Role_Flattened__dlm`` DMO that the CRM kit doesn't ship yet.
export const PIPELINE_STEPS: PipelineStep[] = [
  { id: "crm",     label: "Load CRM",                 layer: "data",       desc: "Accounts, opps, cases",          icon: "diamond-fill" },
  { id: "tdsx",    label: "Build TDSX",               layer: "data",       desc: "Tableau datasource file",        icon: "diamond-outline" },
  { id: "dc",      label: "Push to Data 360",         layer: "data",       desc: "Streams + unified profiles",     icon: "diamond-shaded" },
  { id: "sdm",     label: "C360 SDM",                 layer: "insight",    desc: "Semantic data model",            icon: "circle-fill" },
  // `apps` activates the Insights apps stack: Sales Insights TN
  // Forecasting + Service Insights (with-EDC variant) + the TC26
  // Sample Sales Cloud Dashboard package. The Redesign variant ships
  // as a forward-compat fallback so the deployer can A/B between
  // Redesign and with-EDC. Embedded Sales Insights stays off until
  // the User_Role_Flattened__dlm dependency lands.
  { id: "apps",    label: "Insights Apps",            layer: "activation", desc: "Sales + Service Insights + sample dashboards", icon: "diamond-tilt" },
  // `tabnext` drives Phase 8 (PR 2, 2026-06-03): direct REST/Metadata
  // authoring of the per-pack Tab Next workspace + boards + Tab Next
  // SDM Metrics. Replaces the legacy ATF-Apps deploy chain for
  // non-SI/Service-Insights packs (decision #5 hard-cut). The wizard
  // hides this tile when the selected pack declares
  // ``insights_app: "sales_insights" | "service_insights"`` —
  // those packs stay on the ATF (Phase 6) path.
  { id: "tabnext", label: "Tableau Next (Direct)",  layer: "activation", desc: "Workspace + boards + SDM metrics", icon: "star" },
];

export const RECENT_RUNS: Run[] = [
  { id: "r1", profile: "Cirrus Renewable Energy",  profileId: "cirrus_renewable_energy", volume: "Medium", org: "orgfarm-4a2b", status: "complete", completed: "2h ago",    startedAt: "10:42 AM · today",  duration: "14 min", storiesCount: 7,  owner: "you",            layers: ["crm"],                              cloud: "Sales + Service" },
  { id: "r2", profile: "Northern Trail Outfitters", profileId: "northern_trail",          volume: "Small",  org: "rs-demo-7",    status: "complete", completed: "yesterday", startedAt: "3:18 PM · Oct 17",  duration: "22 min", storiesCount: 9,  owner: "you",            layers: ["crm","dc","sdm","apps","tabnext"],  cloud: "Sales + Service" },
  { id: "r3", profile: "Bolt Bikes D2C",            profileId: "bolt_bikes",              volume: "Large",  org: "orgfarm-4a2b", status: "running",  completed: "in 8 min",  startedAt: "2:05 PM · today",   duration: "~22 min",storiesCount: 5,  owner: "you",            layers: ["crm"],                              cloud: "Sales only" },
  { id: "r4", profile: "Cirrus Renewable Energy",  profileId: "cirrus_renewable_energy", volume: "Large",  org: "ae-emea-12",   status: "failed",   completed: "3 days ago",startedAt: "Oct 14 · 9:22 AM",  duration: "— failed at 38%", storiesCount: 12, owner: "r.singh@sf.com", layers: ["crm","dc"],                          cloud: "Sales + Service", errorMsg: "Bulk API limit hit during Opportunities load (phase 4). Retry with smaller batches." },
  { id: "r5", profile: "Northern Trail Outfitters", profileId: "northern_trail",          volume: "Medium", org: "rs-demo-7",    status: "complete", completed: "5 days ago",startedAt: "Oct 12 · 11:50 AM", duration: "13 min", storiesCount: 6,  owner: "j.park@sf.com",  layers: ["crm","dc","sdm"],                  cloud: "Sales + Service" },
  { id: "r6", profile: "Bolt Bikes D2C",            profileId: "bolt_bikes",              volume: "Tiny",   org: "orgfarm-4a2b", status: "complete", completed: "1 wk ago",  startedAt: "Oct 10 · 4:07 PM",  duration: "3 min",  storiesCount: 2,  owner: "you",            layers: ["tdsx"],                             cloud: "Sales only" },
];

export const TEMPLATES: Template[] = [
  { id: "t_sales_exec",   name: "Sales exec brief",       icon: "diamond-fill",  category: "Sales",    volume: "Large",  packId: "cirrus_renewable_energy", stories: 7,  layers: ["crm","dc","sdm","apps","tabnext"], description: "Pipeline health + forecast + regional gap. Exec-flavored narrative with DC + Tableau Next.", useCount: 412, lastUsed: "2h ago", author: "Q Branch" },
  { id: "t_service_pov",  name: "Service POV",            icon: "circle-fill",   category: "Service",  volume: "Medium", packId: "cirrus_renewable_energy", stories: 6,  layers: ["crm","dc","sdm"],                  description: "CSAT transformation, omni-channel, SLA journey — Service Cloud focus.",                   useCount: 287, lastUsed: "1d ago", author: "Q Branch" },
  { id: "t_quick_screen", name: "Quick screen share",     icon: "lightning",     category: "Fast",     volume: "Small",  packId: "cirrus_renewable_energy", stories: 3,  layers: ["crm"],                             description: "3 greatest-hits stories. Loads in 8 minutes. Good for quick calls.",                      useCount: 1820,lastUsed: "10m ago",author: "Q Branch" },
  { id: "t_retail_exec",  name: "Retail exec review",     icon: "diamond-fill",  category: "Sales",    volume: "Large",  packId: "northern_trail",          stories: 9,  layers: ["crm","dc","sdm","apps"],           description: "NTO · regional gap + seasonal trend. For retail customer execs.",                       useCount: 156, lastUsed: "2d ago", author: "r.singh" },
  { id: "t_d2c_exec",     name: "D2C subscription story", icon: "diamond-fill",  category: "Sales",    volume: "Medium", packId: "bolt_bikes",              stories: 5,  layers: ["crm","dc"],                        description: "Bolt Bikes · subscription tiers + warranty claims. D2C focus.",                          useCount: 89,  lastUsed: "4d ago", author: "j.park" },
  { id: "t_full_stack",   name: "Full-stack tour",        icon: "star",          category: "Complete", volume: "Medium", packId: "cirrus_renewable_energy", stories: 12, layers: ["crm","dc","sdm","apps","tabnext"], description: "Everything — Sales, Service, DC, SDM, apps, Tableau Next. Longer call slot.",            useCount: 62,  lastUsed: "1w ago", author: "Q Branch" },
];

export const PROFILES_LIST: Profile[] = [
  ...PACKS,
  { id: "custom_meridian",  type: "custom", name: "Meridian Healthcare",   industry: "Healthcare / Hospitals", logo: "/assets/pack-cirrus.png", author: "you", updated: "3d ago",   useCount: 4, stories: 5, custom: true,  draft: false },
  { id: "custom_nightfall", type: "custom", name: "Nightfall Security",    industry: "Cybersecurity / B2B",    logo: "/assets/pack-nto.png",    author: "you", updated: "just now", useCount: 0, stories: 0, custom: true,  draft: true },
];

export const PLAYBOOKS: Playbook[] = [
  {
    id: "pb_cirrus_exec",
    title: "Cirrus · Executive briefing",
    profileId: "cirrus_renewable_energy",
    audience: ["CEO","CRO","CFO"],
    length: "35 min",
    stories: 6,
    updated: "Oct 16",
    author: "Q Branch",
    owner: "you",
    description: "The canonical Cirrus exec pitch. Pipeline health → forecast accuracy → regional gap → close.",
    sections: ["intro","stories","qa","close"],
  },
  {
    id: "pb_cirrus_cfo",
    title: "Cirrus · CFO deep-dive",
    profileId: "cirrus_renewable_energy",
    audience: ["CFO"],
    length: "45 min",
    stories: 4,
    updated: "Oct 14",
    author: "r.singh",
    owner: "r.singh",
    description: "ROI, payback period, and cost-to-serve metrics. Heavy on the Tableau Next financial dashboards.",
    sections: ["intro","stories","qa","close"],
  },
  {
    id: "pb_nto_service",
    title: "NTO · Service transformation",
    profileId: "northern_trail",
    audience: ["VP Service","COO"],
    length: "40 min",
    stories: 5,
    updated: "Oct 10",
    author: "j.park",
    owner: "j.park",
    description: "CSAT turnaround narrative for a retail exec. Omni-channel + SLA journey.",
    sections: ["intro","stories","qa","close"],
  },
  {
    id: "pb_bolt_quick",
    title: "Bolt Bikes · Quick screen share",
    profileId: "bolt_bikes",
    audience: ["AE","SE"],
    length: "12 min",
    stories: 3,
    updated: "Oct 09",
    author: "Q Branch",
    owner: "you",
    description: "Short-format D2C demo. Pipeline + product mix + a single use case.",
    sections: ["intro","stories","qa","close"],
  },
];

// ── Playbook content: shared by every playbook for now (the prototype
// also uses a single content blob). Sourced verbatim from prototype/data.js.
export const PLAYBOOK_CONTENT: PlaybookContent = {
  intro: {
    duration: "3 min",
    goal: "Set context. Anchor on one painful problem the audience lives with.",
    script: [
      { type: "say",    text: "Before I show you the platform, tell me — how are you measuring pipeline accuracy today?" },
      { type: "listen", text: "Note their answer. Most will say Excel or a BI tool with a 2-week lag." },
      { type: "say",    text: "That lag is exactly the problem we keep hearing. What I'll show you closes that loop to real-time — here's what that looks like." },
      { type: "click",  text: "Navigate to the Pipeline Health dashboard in Tableau Next." },
    ],
    discovery: [
      "How often does your CRO ask for an updated pipeline?",
      "What's the source of truth when Sales and Finance disagree?",
      "Last time a deal slipped — how long until someone saw it?",
    ],
  },
  stories: [
    {
      id: "pipeline_health",
      title: "Pipeline health",
      duration: "8 min",
      narrative:
        "The CRO walks into a Monday morning staring at six dashboards and a 2-week-old coverage report. By the end of this story, they see one real-time view that flags weak deals, names the rep, and pre-drafts the save motion — without any manual tagging.",
      dataShape: ["380 open opps", "24 reps", "DC signals on 100% of opps", "4 quarters history", "Risk score 0–100"],
      closingLine: "If your CRO had this on Monday morning, they wouldn't need the Friday forecast call.",
      audience: {
        CEO: "Focus on forecast confidence.",
        CFO: "Focus on the dollar gap to plan.",
        CRO: "Focus on rep-level coverage ratios.",
      },
      script: [
        { type: "click",  text: "Open Opportunity list view, sort by Stage." },
        { type: "say",    text: "Every opp here is flagged in real time against the rep's historical conversion rate." },
        { type: "click",  text: "Hover on the yellow indicator — shows the Data 360 risk signal." },
        { type: "say",    text: "This is pulled from their email cadence, stage duration, and past behavior. No manual tagging." },
        { type: "ask",    text: "Which of these signals would've caught the deal that slipped on you last quarter?" },
        { type: "listen", text: "They'll usually name one specific deal. Anchor the rest of the demo on that shape." },
        { type: "click",  text: "Click into the top at-risk opp → show the AI-drafted Save Plan with 3 next actions." },
        { type: "say",    text: "The rep gets this in their Slack every Monday. No dashboard hunting." },
      ],
      objections: [
        { q: "How do you protect rep privacy?", a: "Only aggregate signals leave the CRM; individual messages are never surfaced. Admins control which signals run." },
        { q: "What about data that lives outside Salesforce?", a: "Data 360 ingests any source — Snowflake, Redshift, Segment, custom DBs. We map it once." },
        { q: "What if our reps ignore the flags?", a: "Managers get a rollup of ignored flags; ignored + slipped feeds a coaching signal back to them." },
      ],
    },
    {
      id: "forecast_accuracy",
      title: "Forecast accuracy",
      duration: "6 min",
      narrative:
        "The CFO has been burned four quarters in a row — commit numbers that looked rock-solid collapsed in week 12. The story shows them a walk from commit → best-case → closed with every movement attributed to a specific behavioral signal.",
      dataShape: ["Last 4 quarters of forecast vs actual", "Commit / Best-Case / Pipeline buckets", "1.2% vs 11% variance comparison", "Scenario planner (3 what-ifs)"],
      closingLine: "1.2% vs 11% — that's a board-level accuracy story, not a tool story.",
      audience: {
        CEO: "Story about board-level accuracy.",
        CFO: "Variance to plan over last 4 quarters.",
        CRO: "Commit vs. best-case walk.",
      },
      script: [
        { type: "click", text: "Navigate to the Forecast tab." },
        { type: "say",   text: "Last quarter this customer landed within 1.2% of commit. Before Q Branch, they were at 11%." },
        { type: "click", text: "Open the Commit Walk → show how 7 deals moved between weeks 6–12." },
        { type: "say",   text: "Every movement here is tied to a behavioral signal — not a rep's gut. That's the difference." },
      ],
      objections: [
        { q: "Our deals are too lumpy to forecast.", a: "That's actually where this helps most — it flags the asymmetric outcomes early so you can plan scenarios." },
        { q: "How long until we see this accuracy?", a: "2 quarters of clean data. Quarter 1 is noisy — we use it to calibrate. Quarter 2 is where the variance drops." },
      ],
    },
    {
      id: "regional_gap",
      title: "Regional gap",
      duration: "5 min",
      narrative:
        "West region is 22% under plan and no one agrees why. The story pulls the AI's suggested territory rebalance, layered against historical rep performance and market density — a conversation the territory team has been dreading.",
      dataShape: ["5 regions", "18-month revenue history", "Territory rebalance model", "Market density overlay"],
      closingLine: "The data won't decide for you — but it ends the debate about what the data says.",
      audience: {
        CEO: "Market opportunity framing.",
        CFO: "Revenue attribution framing.",
        CRO: "Territory design framing.",
      },
      script: [
        { type: "click", text: "Tableau Next → Regional Performance board." },
        { type: "say",   text: "West is 22% under plan. Here's what the AI suggests to rebalance quotas." },
        { type: "click", text: "Toggle market density overlay → show where the gap is actually whitespace, not execution." },
        { type: "ask",   text: "When you did your last territory review, did you have this overlay?" },
      ],
      objections: [
        { q: "Can we keep our existing territory tool?", a: "Yes — this augments it. We don't replace your territory-planning process." },
      ],
    },
  ],
  qa: {
    duration: "8 min",
    questions: [
      { q: "How long does implementation actually take?", a: "For a 500-seat Sales Cloud customer, typical time-to-value is 6 weeks. Data 360 foundations in 2 weeks; dashboards and AI in the next 4." },
      { q: "What about our custom objects?", a: "Data 360 maps any object. We have a discovery session where we walk your schema end to end." },
      { q: "Pricing model?", a: "Per-user for Sales/Service; consumption for Data 360; flat for Tableau Next site licenses. I'll bring a scoped quote to the next call." },
      { q: "Do we need to rip out our existing BI?", a: "No. Tableau Next can embed into or coexist with your current BI. Many customers run both for 12 months." },
    ],
  },
  close: {
    duration: "5 min",
    goal: "Establish the next step. Do not leave without a scheduled follow-up.",
    script: [
      { type: "say", text: "Based on what you've seen, the natural next step is a scoped technical deep-dive with your team." },
      { type: "ask", text: "Who on your side should be in that session?" },
      { type: "say", text: "I'll send a Calendly with three slots next week. In parallel, I'll have Q Branch spin up a sandbox with your data shape so we can show this on your numbers, not ours." },
    ],
    nextSteps: [
      "Send scoped deep-dive invite (3 slots)",
      "Spin up a tailored sandbox with their data shape",
      "Share the one-pager Cirrus briefing PDF",
      "Loop in Solution Architect on the thread",
    ],
  },
};

// ── Explore seed data ─────────────────────────────────────────
// Ported verbatim from prototype/data.js so the Explore page works
// fully offline against the mock API. The real /api/explorer/query
// endpoint returns the same shape (chart + insight + suggested
// reshape actions) once the BFF adapter is in place.
export const EXPLORE_QUERIES: ExploreQuery[] = [
  {
    id: "q_winrate",
    question: "What is the win rate for Opportunities by quarter?",
    chart: "line",
    yLabel: "Win rate %",
    xLabel: "Quarter",
    source: "Opportunity",
    series: [
      {
        label: "Win rate",
        data: [
          { x: "Q1", y: 34 },
          { x: "Q2", y: 37 },
          { x: "Q3", y: 41 },
          { x: "Q4", y: 39 },
        ],
      },
    ],
    insight:
      "Win rate peaked in Q3 at 41% and has trended up 5 pts year over year.",
    suggestedActions: [
      "Reduce win rate to 28% (demo: a struggling team)",
      "Push win rate to 55% (demo: after a turnaround)",
      "Make Q4 dip harder — drop to 24%",
      "Flatten it — constant 35% across all quarters",
    ],
  },
  {
    id: "q_pipeline",
    question: "Show me pipeline coverage by rep",
    chart: "bar",
    yLabel: "Coverage (× quota)",
    xLabel: "Rep",
    source: "Opportunity, User",
    series: [
      {
        label: "Coverage",
        data: [
          { x: "Chen", y: 4.2 },
          { x: "Patel", y: 3.8 },
          { x: "Kim", y: 3.1 },
          { x: "Ortiz", y: 2.6 },
          { x: "Walsh", y: 1.9 },
          { x: "Bauer", y: 1.4 },
        ],
      },
    ],
    insight: "Two reps below 2× coverage — at risk of missing quota.",
    suggestedActions: [
      "Make the bottom 2 reps look worse (under 1×)",
      "Level everyone to 3×–4× (healthy team)",
      "Create more spread — superstars vs. strugglers",
    ],
  },
  {
    id: "q_csat",
    question: "CSAT trend by channel over 6 months",
    chart: "multiline",
    yLabel: "CSAT",
    xLabel: "Month",
    source: "Case, Survey",
    series: [
      {
        label: "Phone",
        data: [
          { x: "May", y: 78 },
          { x: "Jun", y: 79 },
          { x: "Jul", y: 81 },
          { x: "Aug", y: 82 },
          { x: "Sep", y: 83 },
          { x: "Oct", y: 85 },
        ],
      },
      {
        label: "Chat",
        data: [
          { x: "May", y: 72 },
          { x: "Jun", y: 75 },
          { x: "Jul", y: 77 },
          { x: "Aug", y: 80 },
          { x: "Sep", y: 83 },
          { x: "Oct", y: 86 },
        ],
      },
      {
        label: "Email",
        data: [
          { x: "May", y: 68 },
          { x: "Jun", y: 69 },
          { x: "Jul", y: 68 },
          { x: "Aug", y: 70 },
          { x: "Sep", y: 71 },
          { x: "Oct", y: 72 },
        ],
      },
    ],
    insight:
      "Chat is the fastest-improving channel, up 14 pts since May. Email is flat.",
    suggestedActions: [
      "Chat CSAT crashes in September (simulate outage)",
      "Push email to catch up with chat",
      "Show a decline story — everything trending down",
    ],
  },
];

export const EXPLORE_HISTORY: ExploreHistoryItem[] = [
  { id: "h1", question: "Win rate for Opportunities", at: "2 min ago", action: null },
  { id: "h2", question: "Pipeline coverage by rep",   at: "12 min ago", action: "Reduced bottom 2 reps to under 1×" },
  { id: "h3", question: "CSAT by channel",            at: "yesterday",  action: "Pushed email to catch up with chat" },
  { id: "h4", question: "Avg deal size by segment",   at: "yesterday",  action: null },
  { id: "h5", question: "Case volume by product",     at: "3 days ago", action: "Added Q3 spike on Bolt X1 model" },
];
